import React, { useState, useMemo, useEffect } from 'react'
import { Button, Pagination, OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
  UploadCloud,
  Image as ImageIcon,
  Database,
  CheckCircle2,
  ArrowRight,
  Grid3x3,
  Grid2x2,
  LayoutGrid,
} from 'lucide-react'
import styled from 'styled-components'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import {
  useFetchAnnotatedContentQuery,
  useGetContentMetadataQuery,
  contentApi,
} from '../../../services/contentApi'
import { useAppDispatch } from '../../../store/hooks'
import ExportSnippetsModal from '../../modals/ExportSnippetsModal'
import SelectDatasetModal from '../../modals/SelectDatasetModal'
import { AnnotationDataBoundingBox, Annotation } from '../../../types/models/annotation'
import AnnotationsGalleryView from '../AnnotationsGalleryView'
import PillsDataset from '../../PillsDataset'
import { useAnnotationStats } from '../../../store/hooks'
import { Content } from '../../../types/models/content'

const ToolContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  gap: 1.5rem;
`

const HeaderSection = styled.div`
  flex-shrink: 0;

  h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    max-width: 800px;
  }
`

const ContentArea = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;
  min-height: 0;
`

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  flex-shrink: 0;
`

const SidebarCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
`

const SidebarCardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DatasetSelector = styled.div`
  cursor: pointer;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &:hover {
    border-color: rgba(102, 126, 234, 0.6);
    background: rgba(102, 126, 234, 0.1);
  }

  .dataset-label {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pills-container {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
`

const SelectionSummary = styled.div`
  .summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &:last-child {
      border-bottom: none;
    }

    .label {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.875rem;
    }

    .value {
      color: #fff;
      font-size: 1rem;
      font-weight: 600;
    }
  }
`

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
`

const GalleryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`

const GalleryControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SizeButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  button {
    background: transparent;
    border: 1px solid rgba(102, 126, 234, 0.2);
    color: #b0b0b0;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    transition: all 0.3s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &.active {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-color: #667eea;
      color: white;
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
      transform: translateY(-1px);
    }

    &:hover:not(.active) {
      background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
      border-color: rgba(102, 126, 234, 0.4);
      color: #ffffff;
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }
  }
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  .pagination {
    margin: 0;
  }

  .page-link {
    background-color: transparent;
    border-color: rgba(102, 126, 234, 0.3);
    color: #fff;
    padding: 0.35rem 0.6rem;
    transition: all 0.3s ease;
    font-size: 0.85rem;
    border-radius: 8px;
    margin: 0 2px;

    &:hover {
      background-color: rgba(102, 126, 234, 0.2);
      border-color: rgba(102, 126, 234, 0.5);
    }
  }

  .page-item.active .page-link {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: #667eea;
    color: #fff;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }

  .page-item.disabled .page-link {
    background-color: transparent;
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.3);
  }
`

const PaginationInfo = styled.div`
  color: #fff;
  font-size: 0.85rem;
  white-space: nowrap;
  padding: 0.35rem 0.75rem;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-weight: 500;
`

const GalleryContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
`

const ButtonBase = Button as unknown as React.ComponentType<
  React.ButtonHTMLAttributes<HTMLButtonElement>
>

const ExportButton = styled(ButtonBase)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

interface SendToClassificationToolProps {
  onBack: () => void
}

const ITEMS_PER_PAGE = 200

const SendToClassificationTool: React.FC<SendToClassificationToolProps> = ({ onBack }) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const [imagesPerRow, setImagesPerRow] = useState(3) // 3, 2, or 1 per row
  const [showExport, setShowExport] = useState(false)
  const [showDatasetModal, setShowDatasetModal] = useState(false)
  const [selectedContent, setSelectedContent] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [isSelectingAll, setIsSelectingAll] = useState(false)
  const [allFetchedContent, setAllFetchedContent] = useState<Content[]>([])
  const [selectAllProgress, setSelectAllProgress] = useState({ current: 0, total: 0 })
  const [onlyWithBoxes, setOnlyWithBoxes] = useState(false)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: selectedProjectId || '',
    },
    { skip: !selectedProjectId }
  )

  const availableDatasets = useMemo(() => metadata?.metadata?.datasource || [], [metadata])

  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])

  useEffect(() => {
    if (availableDatasets.length > 0 && selectedDatasets.length === 0) {
      setSelectedDatasets(availableDatasets)
    }
  }, [availableDatasets, selectedDatasets.length])

  // Get annotation stats to get accurate total annotated images count
  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    selectedDatasets.length === 0 || selectedDatasets.length === availableDatasets.length
      ? availableDatasets
      : selectedDatasets,
    { skip: !selectedProjectId || !project?.datasetid || selectedDatasets.length === 0 }
  )

  const metadataFilter = useMemo(() => {
    if (selectedDatasets.length === 0 || selectedDatasets.length === availableDatasets.length) {
      return undefined
    }
    return { datasource: selectedDatasets }
  }, [selectedDatasets, availableDatasets.length])

  const { data: annotatedData, isLoading } = useFetchAnnotatedContentQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      page: currentPage - 1,
      limit: ITEMS_PER_PAGE,
      metadata_filter: metadataFilter,
      sort_key: 'updated_at',
      sort_val: -1,
    },
    { skip: !selectedProjectId || !project?.datasetid || selectedDatasets.length === 0 }
  )

  // Function to fetch all pages of annotated content with parallel requests
  const fetchAllAnnotatedContent = async () => {
    if (!selectedProjectId || !project?.datasetid || selectedDatasets.length === 0) return

    setIsSelectingAll(true)
    setAllFetchedContent([])
    setSelectAllProgress({ current: 0, total: 0 })

    try {
      const pageSize = 1000
      const allContent: Content[] = []

      // Use annotation stats for accurate total count (not the API response count which is per-page)
      const totalCount = annoStats?.statistics?.total_annotated_images || 0
      if (totalCount === 0) {
        setAllFetchedContent([])
        setIsSelectingAll(false)
        return
      }

      const totalPages = Math.ceil(totalCount / pageSize)
      setSelectAllProgress({ current: 0, total: totalPages })

      console.log(
        'Select All: Total annotated images from stats:',
        totalCount,
        'Total pages:',
        totalPages
      )

      // Fetch all pages in parallel batches (5 at a time)
      const batchSize = 5

      for (let batchStart = 0; batchStart < totalPages; batchStart += batchSize) {
        const batchPromises = []
        const batchEnd = Math.min(batchStart + batchSize, totalPages)

        for (let pageNum = batchStart; pageNum < batchEnd; pageNum++) {
          batchPromises.push(
            dispatch(
              contentApi.endpoints.fetchAnnotatedContent.initiate({
                project_id: selectedProjectId,
                dataset_id: project.datasetid,
                page: pageNum,
                limit: pageSize,
                metadata_filter: metadataFilter,
                sort_key: 'updated_at',
                sort_val: -1,
              })
            ).unwrap()
          )
        }

        const batchResults = await Promise.all(batchPromises)

        batchResults.forEach((response, index) => {
          if (response.content && response.content.length > 0) {
            allContent.push(...response.content)
            console.log(
              `Select All: Fetched page ${batchStart + index}, got ${response.content.length} items`
            )
          } else {
            console.log(`Select All: Page ${batchStart + index} returned no content`)
          }
        })

        setSelectAllProgress({
          current: Math.min(batchEnd, totalPages),
          total: totalPages,
        })
      }

      console.log('Select All: Total items fetched:', allContent.length)

      setAllFetchedContent(allContent)
    } catch (error) {
      console.error('Error fetching all content:', error)
      setIsSelectingAll(false)
      setSelectAllProgress({ current: 0, total: 0 })
    }
  }

  // Match labeling view by default: use all annotated images (the API already returns annotated
  // content). When "Only images with boxes" is enabled, we filter to those with at least one
  // bounding box for the current page.
  const annotatedContentFromApi = useMemo(() => {
    const pageContent = annotatedData?.content || []
    if (!onlyWithBoxes) return pageContent

    return pageContent.filter((c) => {
      const boxes = (c.annotation || [])
        .flatMap((a: Annotation) => [
          ...((a.metadata?.bounding_boxes || []) as AnnotationDataBoundingBox[]),
          ...((a.metadata?.oriented_bounding_boxes || []) as AnnotationDataBoundingBox[]),
        ])
        .filter((b: AnnotationDataBoundingBox | undefined): b is AnnotationDataBoundingBox => !!b)
      return boxes.length > 0
    })
  }, [annotatedData?.content, onlyWithBoxes])

  // Get total annotated images from annotation stats API (accurate count)
  const totalAnnotatedImages = useMemo(() => {
    // Use annotation stats for accurate total count
    if (annoStats?.statistics?.total_annotated_images !== undefined) {
      return annoStats.statistics.total_annotated_images
    }
    // Fallback to API response count (less accurate, might be per-page)
    if (annotatedData?.count !== undefined) {
      return annotatedData.count
    }
    return 0
  }, [annoStats?.statistics?.total_annotated_images, annotatedData?.count])

  // Calculate total images with bounding boxes (for display / summary only).
  // Pagination uses totalAnnotatedImages so it matches labeling view expectations.
  const totalImagesWithBoxes = useMemo(() => {
    // If we've fetched all content (from Select All), use the actual count
    if (allFetchedContent.length > 0) {
      const withBoxes = allFetchedContent.filter((c) => {
        const boxes = (c.annotation || [])
          .flatMap((a: Annotation) => [
            ...((a.metadata?.bounding_boxes || []) as AnnotationDataBoundingBox[]),
            ...((a.metadata?.oriented_bounding_boxes || []) as AnnotationDataBoundingBox[]),
          ])
          .filter((b: AnnotationDataBoundingBox | undefined): b is AnnotationDataBoundingBox => !!b)
        return boxes.length > 0
      })
      return withBoxes.length
    }

    // Otherwise, estimate based on ratio from current page
    if (annotatedData?.content && annotatedData.content.length > 0) {
      const withBoxesOnPage = (annotatedData.content || []).filter((c) => {
        const boxes = (c.annotation || [])
          .flatMap((a: Annotation) => [
            ...((a.metadata?.bounding_boxes || []) as AnnotationDataBoundingBox[]),
            ...((a.metadata?.oriented_bounding_boxes || []) as AnnotationDataBoundingBox[]),
          ])
          .filter((b: AnnotationDataBoundingBox | undefined): b is AnnotationDataBoundingBox => !!b)
        return boxes.length > 0
      }).length
      const ratio = withBoxesOnPage / annotatedData.content.length
      return Math.round(totalAnnotatedImages * ratio)
    }

    return 0
  }, [allFetchedContent, annotatedData, totalAnnotatedImages])

  const annotatedContent = annotatedContentFromApi

  // When filtering "only with boxes", use the estimated total with boxes for pagination;
  // otherwise use the full annotated images total so we match labeling view.
  const totalItemsForPaging = onlyWithBoxes ? totalImagesWithBoxes : totalAnnotatedImages
  const totalPages = Math.max(1, Math.ceil(totalItemsForPaging / ITEMS_PER_PAGE))

  // Show range based on how many images are actually visible on this page,
  // not the theoretical max per page, so the count matches what you can select.
  const startItem = totalItemsForPaging === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1
  const endItem =
    totalItemsForPaging === 0 ? 0 : startItem + Math.max(annotatedContent.length - 1, 0)

  const renderDatasetPills = () => {
    if (!selectedDatasets?.length) {
      return <PillsDataset tiny datasetNames={['All']} disabled={true} />
    }

    const allSelected =
      availableDatasets.length > 0 &&
      selectedDatasets.length === availableDatasets.length &&
      availableDatasets.every((ds) => selectedDatasets.includes(ds))

    if (allSelected) {
      return <PillsDataset tiny datasetNames={['All']} disabled={true} />
    }

    const visibleDatasets = selectedDatasets.slice(0, 2)
    const remainingCount = selectedDatasets.length - 2

    return (
      <div className="pills-container">
        {visibleDatasets.map((dataset: string, index: number) => (
          <PillsDataset key={index} tiny datasetNames={[dataset]} disabled={true} />
        ))}
        {remainingCount > 0 && (
          <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
            +{remainingCount} more
          </span>
        )}
      </div>
    )
  }

  // Build export items from all selected content
  // Use allFetchedContent if available (from "Select All"), otherwise use current page content
  const exportItems = useMemo(() => {
    const items: Array<{ contentId: string; boxes: AnnotationDataBoundingBox[] }> = []

    // Use all fetched data if we have it (from Select All), otherwise use current page
    const contentToProcess = allFetchedContent.length > 0 ? allFetchedContent : annotatedContent

    for (const c of contentToProcess) {
      if (selectedContent.has(c.id)) {
        const boxes = (c.annotation || [])
          .flatMap((a: Annotation) => [
            ...((a.metadata?.bounding_boxes || []) as AnnotationDataBoundingBox[]),
            ...((a.metadata?.oriented_bounding_boxes || []) as AnnotationDataBoundingBox[]),
          ])
          .filter((b: AnnotationDataBoundingBox | undefined): b is AnnotationDataBoundingBox => !!b)
        if (boxes.length > 0) {
          items.push({ contentId: c.id, boxes })
        }
      }
    }
    return items
  }, [annotatedContent, allFetchedContent, selectedContent])

  // When all content is loaded for "Select All", extract IDs with bounding boxes
  useEffect(() => {
    if (isSelectingAll && allFetchedContent.length > 0) {
      const allWithBoxes = allFetchedContent.filter((c) => {
        const boxes = (c.annotation || [])
          .flatMap((a: Annotation) => [
            ...((a.metadata?.bounding_boxes || []) as AnnotationDataBoundingBox[]),
            ...((a.metadata?.oriented_bounding_boxes || []) as AnnotationDataBoundingBox[]),
          ])
          .filter((b: AnnotationDataBoundingBox | undefined): b is AnnotationDataBoundingBox => !!b)
        return boxes.length > 0
      })
      setSelectedContent(new Set(allWithBoxes.map((c) => c.id)))
      setIsSelectingAll(false)
    }
  }, [isSelectingAll, allFetchedContent])

  const handleContentClick = (content: { id: string }) => {
    setSelectedContent((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(content.id)) {
        newSet.delete(content.id)
      } else {
        newSet.add(content.id)
      }
      return newSet
    })
  }

  // Global select all / clear all
  const handleSelectAll = () => {
    fetchAllAnnotatedContent()
  }

  const handleDeselectAll = () => {
    setSelectedContent(new Set())
    setAllFetchedContent([])
  }

  // Per-page select (current page only)
  const isCurrentPageFullySelected = useMemo(() => {
    if (!annotatedContent || annotatedContent.length === 0) return false
    return annotatedContent.every((c) => selectedContent.has(c.id))
  }, [annotatedContent, selectedContent])

  const handleToggleSelectPage = () => {
    if (!annotatedContent || annotatedContent.length === 0) return

    setSelectedContent((prev) => {
      const newSet = new Set(prev)
      if (isCurrentPageFullySelected) {
        // Deselect only current page
        annotatedContent.forEach((c) => {
          if (c?.id) {
            newSet.delete(c.id)
          }
        })
      } else {
        // Select all on current page
        annotatedContent.forEach((c) => {
          if (c?.id) {
            newSet.add(c.id)
          }
        })
      }
      return newSet
    })
  }

  // Calculate total snippets from all selected content
  const totalSnippets = useMemo(() => {
    return exportItems.reduce((sum, item) => sum + item.boxes.length, 0)
  }, [exportItems])

  const canExport = exportItems.length > 0

  if (project?.annotation_type === 'classification') {
    return (
      <ToolContainer>
        <HeaderSection>
          <h2>Send to Classification</h2>
          <p>This tool is only available for object detection projects</p>
        </HeaderSection>
      </ToolContainer>
    )
  }

  return (
    <ToolContainer>
      <HeaderSection>
        <h2>
          <UploadCloud size={28} />
          Send to Classification
        </h2>
        <p>
          Select annotated images with bounding boxes to export as snippets to a classification
          project. Browse through your datasets, select images, and export the bounding box regions
          for classification training.
        </p>
      </HeaderSection>

      <ContentArea>
        <Sidebar>
          <SidebarCard>
            <SidebarCardTitle>
              <Database size={18} />
              Datasets
            </SidebarCardTitle>
            <OverlayTrigger placement="right" overlay={<Tooltip>Click to select datasets</Tooltip>}>
              <DatasetSelector onClick={() => setShowDatasetModal(true)}>
                <div className="dataset-label">
                  <span>Select Dataset(s)</span>
                </div>
                {renderDatasetPills()}
              </DatasetSelector>
            </OverlayTrigger>
          </SidebarCard>

          {selectedDatasets.length > 0 && (
            <SidebarCard>
              <SidebarCardTitle>
                <CheckCircle2 size={18} />
                Selection Summary
              </SidebarCardTitle>
              <div className="d-flex flex-column gap-2 mb-3">
                <Button variant="primary" size="sm" onClick={handleSelectAll} className="w-100">
                  Select All Images
                </Button>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={handleDeselectAll}
                  className="w-100"
                >
                  Deselect All
                </Button>
              </div>
              <SelectionSummary>
                <div className="summary-item">
                  <span className="label">Images Selected</span>
                  <span className="value">{selectedContent.size}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Snippets Ready</span>
                  <span className="value">{totalSnippets}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Total Annotated</span>
                  <span className="value">{totalAnnotatedImages.toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <span className="label">With Bounding Boxes</span>
                  <span className="value">{totalImagesWithBoxes.toLocaleString()}</span>
                </div>
              </SelectionSummary>
              {isSelectingAll && (
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <div
                    className="text-white-50"
                    style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}
                  >
                    Loading all images...
                  </div>
                  {selectAllProgress.total > 0 && (
                    <>
                      <div
                        className="text-white-50"
                        style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}
                      >
                        Page {selectAllProgress.current} of {selectAllProgress.total}
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: '4px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${
                              (selectAllProgress.current / selectAllProgress.total) * 100
                            }%`,
                            height: '100%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </SidebarCard>
          )}

          {selectedDatasets.length > 0 && (
            <SidebarCard>
              <div className="d-flex flex-column gap-2">
                <ExportButton
                  onClick={() => setShowExport(true)}
                  disabled={!canExport}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  <UploadCloud size={18} />
                  Export to Classification
                  <ArrowRight size={16} />
                </ExportButton>
                {!canExport && selectedContent.size > 0 && (
                  <p
                    style={{
                      marginTop: '0.75rem',
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      textAlign: 'center',
                    }}
                  >
                    Selected images must have bounding boxes
                  </p>
                )}
              </div>
            </SidebarCard>
          )}
        </Sidebar>

        <MainContent>
          {availableDatasets.length === 0 ? (
            <GalleryContainer>
              <div className="text-center py-5">
                <Database size={48} className="text-white-50 mb-3" />
                <p className="text-white-50">No datasets available in this project</p>
              </div>
            </GalleryContainer>
          ) : selectedDatasets.length === 0 ? (
            <GalleryContainer>
              <div className="text-center py-5">
                <Database size={48} className="text-white-50 mb-3" />
                <p className="text-white-50">Please select datasets to view images</p>
              </div>
            </GalleryContainer>
          ) : (
            <>
              <GalleryHeader>
                <GalleryControls>
                  <SizeButtons>
                    <button
                      className={imagesPerRow === 3 ? 'active' : ''}
                      onClick={() => setImagesPerRow(3)}
                      title="3 per row"
                    >
                      <Grid3x3 size={16} />
                    </button>
                    <button
                      className={imagesPerRow === 2 ? 'active' : ''}
                      onClick={() => setImagesPerRow(2)}
                      title="2 per row"
                    >
                      <Grid2x2 size={16} />
                    </button>
                    <button
                      className={imagesPerRow === 1 ? 'active' : ''}
                      onClick={() => setImagesPerRow(1)}
                      title="1 per row"
                    >
                      <LayoutGrid size={16} />
                    </button>
                  </SizeButtons>
                  {annotatedContent.length > 0 && (
                    <div className="form-check ms-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="onlyWithBoxesCheckbox"
                        checked={onlyWithBoxes}
                        onChange={(e) => {
                          setOnlyWithBoxes(e.target.checked)
                          setCurrentPage(1)
                        }}
                      />
                      <label
                        className="form-check-label text-white-50"
                        htmlFor="onlyWithBoxesCheckbox"
                      >
                        Only images with boxes
                      </label>
                    </div>
                  )}
                  {annotatedContent.length > 0 && (
                    <div className="form-check ms-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="selectPageCheckbox"
                        checked={isCurrentPageFullySelected}
                        onChange={handleToggleSelectPage}
                      />
                      <label
                        className="form-check-label text-white-50"
                        htmlFor="selectPageCheckbox"
                      >
                        Select page ({annotatedContent.length} images)
                      </label>
                    </div>
                  )}
                </GalleryControls>
                <PaginationContainer>
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Item active>{currentPage}</Pagination.Item>
                    <Pagination.Next
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                  <PaginationInfo>
                    {startItem}-{endItem} of {totalImagesWithBoxes}
                  </PaginationInfo>
                </PaginationContainer>
              </GalleryHeader>

              <GalleryContainer>
                {isLoading ? (
                  <div className="text-center py-5">
                    <p className="text-white-50">Loading annotated images...</p>
                  </div>
                ) : annotatedContent.length === 0 ? (
                  <div className="text-center py-5">
                    <ImageIcon size={48} className="text-white-50 mb-3" />
                    <p className="text-white-50">No annotated images found in selected datasets</p>
                  </div>
                ) : (
                  <AnnotationsGalleryView
                    content={annotatedContent}
                    isContentLoading={isLoading}
                    imagesPerRow={imagesPerRow}
                    onContentSelect={handleContentClick}
                    selectedContentIds={selectedContent}
                    selectable={true}
                  />
                )}
              </GalleryContainer>
            </>
          )}
        </MainContent>
      </ContentArea>

      {showExport && (
        <ExportSnippetsModal
          show={showExport}
          onHide={() => setShowExport(false)}
          items={exportItems}
        />
      )}

      <SelectDatasetModal
        show={showDatasetModal}
        onHide={() => setShowDatasetModal(false)}
        handleAcceptSelections={(sources) => {
          setSelectedDatasets(sources)
          setShowDatasetModal(false)
          setSelectedContent(new Set())
        }}
        selectedSources={selectedDatasets}
        canCreate={false}
        singleSelect={false}
      />
    </ToolContainer>
  )
}

export default SendToClassificationTool
